import React from "react";
import {
    Box, Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Chip,
    Avatar,
} from "@mui/material";
import { CiCalendar } from "react-icons/ci";
import { GiSandsOfTime } from "react-icons/gi";
import { useParams } from "react-router-dom";
import { useGetMilestoneListQuery } from "store/slices/apis/milestoneApiSlice";
import SchedulePanel from "./SchedulePanel";
import { enterpriseTokens } from "styles/theme";

const formatDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).replace(" ", " ").replace(",", "");
};
const getStatusColor = (status) => {
    switch (status) {
        case "Completed":
            return "#DCFCE7";
        case "Delayed":
            return "#FEE2E2";
        case "Future":
            return "#FEF3C7";
        default:
            return "#D0D5DD";   // Grey (unknown)
    }
};
const getStatusTextColor = (status) => {
    switch (status) {
        case "Completed":
            return enterpriseTokens.success;
        case "Delayed":
            return enterpriseTokens.danger;
        case "Future":
            return enterpriseTokens.warning;
        default:
            return "#101828";   // Grey (unknown)
    }
};

const UpcomingMilestone = () => {
    const { projectId } = useParams();
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('milestone');
    const [selected, setSelected] = React.useState([]);

    const { data: milestoneList, isLoading } = useGetMilestoneListQuery({pid: projectId});
    const milestones = milestoneList?.data || [];

    const createSortHandler = (field) => {
        const isAsc = orderBy === field && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(field);
    };


    const isSelected = (id) => selected.indexOf(id) !== -1;

    if (milestones.length === 0) {
        return null
    }

    return (
        <SchedulePanel
            eyebrow="Milestone readiness"
            title="Upcoming Commitments"
            subtitle="Review near-term milestones and intervene before target dates are missed."
            bodySx={{ pb: 1 }}
        >
                    <Box>
                        <TableContainer
                            sx={{

                                // boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                overflowY: 'auto',
                                //maxHeight: '220px',
                                p: 0
                            }}
                        >
                            <Table sx={{ p: 0, width: "100%" }} >
                                <TableHead sx={{ backgroundColor: "#F8FAFC", borderTop: `1px solid ${enterpriseTokens.lineSoft}`, width: "100%" }}>
                                    <TableRow>
                                        <TableCell sx={{
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            color: enterpriseTokens.slate,
                                            lineHeight: "140%",
                                            fontFamily: "Inter, Outfit, sans-serif",
                                            letterSpacing: "1%"
                                        }}
                                        >
                                            <TableSortLabel
                                                active={orderBy === "milestone"}
                                                direction={orderBy === "milestone" ? order : 'asc'}
                                                onClick={() => createSortHandler("milestone")}
                                            >
                                                Milestone
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sx={{
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            color: enterpriseTokens.slate,
                                            lineHeight: "140%",
                                            fontFamily: "Inter, Outfit, sans-serif",
                                            letterSpacing: "1%"
                                        }}>
                                            Completion %
                                        </TableCell>
                                        <TableCell align="center" sx={{
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            color: enterpriseTokens.slate,
                                            lineHeight: "140%",
                                            fontFamily: "Inter, Outfit, sans-serif",
                                            letterSpacing: "1%"
                                        }}>
                                            Days to Complete
                                        </TableCell>
                                        <TableCell align="center" sx={{
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            color: enterpriseTokens.slate,
                                            lineHeight: "140%",
                                            fontFamily: "Inter, Outfit, sans-serif",
                                            letterSpacing: "1%"
                                        }}>
                                            Status
                                        </TableCell>
                                        <TableCell align="center" sx={{
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            color: enterpriseTokens.slate,
                                            lineHeight: "140%",
                                            fontFamily: "Inter, Outfit, sans-serif",
                                            letterSpacing: "1%"
                                        }}>
                                            Current Run Rate
                                        </TableCell>
                                        <TableCell align="center" sx={{
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            color: enterpriseTokens.slate,
                                            lineHeight: "140%",
                                            fontFamily: "Inter, Outfit, sans-serif",
                                            letterSpacing: "1%"
                                        }}>
                                            Required Run Rate
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {!isLoading && milestones?.map((row, index) => {
                                        const isItemSelected = isSelected(row.id);
                                        const labelId = `enhanced-table-checkbox-${index}`;

                                        return (
                                            <TableRow
                                                key={row.id}
                                                sx={{
                                                    '&:last-child td, &:last-child th': { border: 0 },
                                                    '&:hover': { backgroundColor: '#F8FAFC' },
                                                    height: '52px'
                                                }}
                                                role="checkbox"
                                                aria-checked={isItemSelected}
                                                tabIndex={-1}
                                                selected={isItemSelected}
                                            >
                                                <TableCell >
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                                        <Avatar
                                                            sx={{
                                                                bgcolor: enterpriseTokens.primarySoft,
                                                                width: "36px",
                                                                height: "36px",
                                                                border: "none",
                                                            }}
                                                        >
                                                            <Typography sx={{
                                                                    fontWeight: 600,
                                                                    fontSize: "15px",
                                                                    color: enterpriseTokens.primaryDark,
                                                                    lineHeight: "140%",
                                                                    fontFamily: "Inter, Outfit, sans-serif",
                                                                letterSpacing: "1%"
                                                            }}>
                                                                {index + 1}
                                                            </Typography>
                                                        </Avatar>
                                                        <Box sx={{ display: "flex", flexDirection: "column", gap: "5px" }} >
                                                            <Typography sx={{
                                                                fontWeight: 600,
                                                                fontSize: "12px",
                                                                color: enterpriseTokens.navy,
                                                                lineHeight: "140%",
                                                                fontFamily: "Inter, Outfit, sans-serif",
                                                                letterSpacing: "1%"
                                                            }}>
                                                                {row.milestone} ({row.milestoneValue} Cr.)
                                                            </Typography>
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                                                <Typography sx={{
                                                                    fontWeight: 400,
                                                                    fontSize: "12px",
                                                                    color: enterpriseTokens.muted,
                                                                    lineHeight: "140%",
                                                                    fontFamily: "Outfit, sans-serif",
                                                                    letterSpacing: "1%"
                                                                }}>
                                                                    Due Date
                                                                </Typography>
                                                                <Chip
                                                                    icon={<CiCalendar size={16} color="#433F5E" />}
                                                                    label={formatDate(row.dueDate)}
                                                                    sx={{
                                                                        padding: "8px 4px",
                                                                        fontSize: "11px",
                                                                        fontWeight: 600,
                                                                        color: "#433F5E",
                                                                        bgcolor: "#F8FAFC",
                                                                        border: `1px solid ${enterpriseTokens.lineSoft}`,
                                                                        fontFamily: "Inter, Outfit, sans-serif",
                                                                    }}
                                                                />
                                                            </Box>
                                                            {row?.curePeriodDueDate && <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                                                <Typography sx={{
                                                                    fontWeight: 400,
                                                                    fontSize: "12px",
                                                                    color: enterpriseTokens.muted,
                                                                    lineHeight: "140%",
                                                                    fontFamily: "Outfit, sans-serif",
                                                                    letterSpacing: "1%"
                                                                }}>
                                                                    With Cure Period
                                                                </Typography>
                                                                <Chip
                                                                    icon={<CiCalendar size={16} color="#433F5E" />}
                                                                    label={formatDate(row?.curePeriodDueDate)}
                                                                    sx={{
                                                                        padding: "8px 4px",
                                                                        fontSize: "11px",
                                                                        fontWeight: 600,
                                                                        color: "#433F5E",
                                                                        bgcolor: "#F8FAFC",
                                                                        border: `1px solid ${enterpriseTokens.lineSoft}`,
                                                                        fontFamily: "Inter, Outfit, sans-serif",
                                                                    }}
                                                                />
                                                            </Box>}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography sx={{
                                                        padding: "2px 6px",
                                                        fontSize: "11px",
                                                        fontWeight: 600,
                                                        color: enterpriseTokens.slate,
                                                        fontFamily: "Inter, Outfit, sans-serif",
                                                    }}>
                                                        {row.percentageComplete} %
                                                    </Typography>
                                                </TableCell>

                                                <TableCell align="center" >
                                                    <Chip
                                                        icon={<GiSandsOfTime size={16} color={getStatusTextColor(row.status)} />}
                                                        label={row.status == "Completed" ? "Completed" : row.daysToComplete}
                                                        sx={{
                                                            backgroundColor: getStatusColor(row.status),
                                                            padding: "2px 8px",
                                                            fontSize: "12px",
                                                            fontWeight: 700,
                                                            color: getStatusTextColor(row.status),//"#FF9D27",
                                                            fontFamily: "Inter, Outfit, sans-serif",
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography sx={{
                                                        padding: "2px 6px",
                                                        fontSize: "11px",
                                                        fontWeight: 600,
                                                        color: enterpriseTokens.slate,
                                                        fontFamily: "Inter, Outfit, sans-serif",
                                                    }}>
                                                        {row.status}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography sx={{
                                                        padding: "2px 6px",
                                                        fontSize: "11px",
                                                        fontWeight: 700,
                                                        color: enterpriseTokens.slate,
                                                        fontFamily: "Inter, Outfit, sans-serif",
                                                    }}>
                                                        {row.currentRunRate || "N/A"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography sx={{
                                                        padding: "2px 6px",
                                                        fontSize: "11px",
                                                        fontWeight: 700,
                                                        color: enterpriseTokens.slate,
                                                        fontFamily: "Inter, Outfit, sans-serif",
                                                    }}>
                                                        {row.requiredRunRate || "N/A"}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
        </SchedulePanel>
    )
}
export default UpcomingMilestone;
